import { t } from '@lingui/macro'
import { Form, Formik, FormikValues } from 'formik'
import { useState } from 'react'
import { object, string } from 'yup'

import { Button, Input, Message, Space } from '@/components'
import { ErrorMessages } from '@/constants'
import useAuth from '@/hooks/useAuth'
import { post } from '@/rest/api'

const loginSchema = object().shape({
  email: string().email(ErrorMessages.INVALID_EMAIL).required(ErrorMessages.REQUIRED_FIELD),
  password: string().required(ErrorMessages.REQUIRED_FIELD),
})

type Props = {
  nextStep: () => void
}

const Login: React.FC<React.PropsWithChildren<Props>> = ({ nextStep }) => {
  const [loginError, setLoginError] = useState<string>()
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()

  const handleSubmit = async (values: FormikValues): Promise<void> => {
    setLoading(true)
    setLoginError(undefined)

    const { email, password } = values

    await post('/auth/signin', {
      user: {
        email,
        password,
      },
    })
      .then(({ data }) => {
        login(data.token, null, nextStep)
      })
      .catch(({ response }) => {
        setLoading(false)
        setLoginError(response.status === 401 ? t`Email or password are incorrect` : t`Something went wrong`)
      })
  }

  return (
    <Formik initialValues={{ email: '', password: '' }} validationSchema={loginSchema} onSubmit={handleSubmit}>
      {({ isSubmitting }) => (
        <Form>
          <Space y={6}>
            {loginError ? <Message type="error">{loginError}</Message> : null}

            <div className="grid grid-cols-2 gap-x-6 gap-y-12">
              <div className="col-span-2">
                <Input name="email" label={t`Email address`} />
              </div>

              <div className="col-span-2">
                <Input name="password" label={t`Password`} type="password" />
              </div>

              <div className="col-span-2">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={isSubmitting || loading}
                  loading={isSubmitting || loading}
                  block
                >
                  {isSubmitting ? t`Please wait ...` : t`Sign in`}
                </Button>
              </div>
            </div>
          </Space>
        </Form>
      )}
    </Formik>
  )
}

export default Login
